import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import type { Contact, ContactSearchResult } from '@/services/prospects.api';

export type ContactDraft = {
  firstName: string;
  lastName: string;
  role: Contact['role'];
  email: string;
  phoneMobile: string;
  phoneLandline: string;
  isPrimary: boolean;
  /** Observation libre propre à ce rattachement (vide = pas d'observation). */
  observations: string;
};

const EMPTY: ContactDraft = {
  firstName: '',
  lastName: '',
  role: 'other',
  email: '',
  phoneMobile: '',
  phoneLandline: '',
  isPrimary: false,
  observations: '',
};

interface Props {
  /** Titre affiché (par défaut "Nouveau contact"). */
  title?: string;
  /** Pré-remplissage (mode édition). */
  initial?: Partial<ContactDraft>;
  /**
   * Emails déjà utilisés par d'autres contacts du même prospect. Si l'email
   * saisi y figure, un warning non bloquant est affiché. Cas légitime :
   * deux interlocuteurs partageant une adresse générique (contact@pme.fr).
   */
  existingEmails?: string[];
  /**
   * Nombre de prospects auxquels la personne éditée est rattachée. Si > 1, on
   * affiche une note : nom/email/téléphone sont partagés (l'édition les
   * propage à toutes les entreprises), le rôle/principal restent locaux.
   */
  linkedProspectsCount?: number;
  /**
   * Si fourni (mode création), affiche en haut une combobox Stripe-like qui
   * cherche parmi les contacts existants (cloisonnés) pour en rattacher un.
   * `onAttachExisting` est appelé quand l'utilisateur en sélectionne un.
   */
  searchExisting?: (q: string) => Promise<ContactSearchResult[]>;
  onAttachExisting?: (contact: ContactSearchResult) => Promise<void> | void;
  /**
   * Callback à la validation du formulaire « nouveau contact / édition ».
   * Retourne une Promise : si elle rejette, l'erreur est affichée et la modale
   * reste ouverte. La fermeture est gérée par l'appelant.
   */
  onSubmit: (draft: ContactDraft) => Promise<void>;
  onClose: () => void;
  /**
   * Niveau z-index (par défaut `z-[55]` pour passer au-dessus d'une modale
   * parente en `z-50` comme `CreateProspectModal`).
   */
  zClass?: string;
}

export function ContactFormModal({
  title = 'Nouveau contact',
  initial,
  existingEmails = [],
  linkedProspectsCount,
  searchExisting,
  onAttachExisting,
  onSubmit,
  onClose,
  zClass = 'z-[55]',
}: Props) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });

  const [draft, setDraft] = useState<ContactDraft>({ ...EMPTY, ...initial });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showAttach = !!searchExisting && !!onAttachExisting;
  const sharedPerson = (linkedProspectsCount ?? 0) > 1;

  const emailLower = draft.email.trim().toLowerCase();
  const duplicateEmail = useMemo(() => {
    if (!emailLower) return false;
    return existingEmails.some((e) => e.trim().toLowerCase() === emailLower);
  }, [emailLower, existingEmails]);

  const canSubmit = !submitting && !!draft.firstName.trim() && !!draft.lastName.trim();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        role: draft.role,
        email: draft.email.trim(),
        phoneMobile: draft.phoneMobile.trim(),
        phoneLandline: draft.phoneLandline.trim(),
        isPrimary: draft.isPrimary,
        observations: draft.observations.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  async function handleAttach(contact: ContactSearchResult) {
    setError(null);
    try {
      await onAttachExisting!(contact);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  return (
    <div
      className={`fixed inset-0 ${zClass} flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in`}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg bg-white shadow-modal animate-scale-in"
      >
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="flex items-center justify-between border-b border-border-subtle pb-4">
            <h2 className="text-lg font-semibold text-brand">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {showAttach && (
            <ExistingContactCombobox search={searchExisting!} onSelect={handleAttach} />
          )}

          {showAttach && (
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-border-subtle" />
              <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
                ou créer un nouveau contact
              </span>
              <span className="h-px flex-1 bg-border-subtle" />
            </div>
          )}

          {sharedPerson && (
            <Banner tone="info">
              Cette personne est rattachée à {linkedProspectsCount} entreprises. Modifier le nom,
              l&apos;email ou le téléphone met à jour la fiche partagée ; le rôle et le statut «
              principal » ne concernent que ce prospect.
            </Banner>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-firstName" required>
                Prénom
              </Label>
              <Input
                id="c-firstName"
                value={draft.firstName}
                onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="c-lastName" required>
                Nom
              </Label>
              <Input
                id="c-lastName"
                value={draft.lastName}
                onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="c-email">Email</Label>
            <Input
              id="c-email"
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
            {duplicateEmail && (
              <p className="mt-1 text-xs text-warning-600">
                Un autre contact de ce prospect utilise déjà cet email. Vérifiez que ce n&apos;est
                pas un doublon.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-mobile">Mobile</Label>
              <Input
                id="c-mobile"
                type="tel"
                value={draft.phoneMobile}
                onChange={(e) => setDraft({ ...draft, phoneMobile: e.target.value })}
                placeholder="06 12 34 56 78"
              />
            </div>
            <div>
              <Label htmlFor="c-landline">Fixe</Label>
              <Input
                id="c-landline"
                type="tel"
                value={draft.phoneLandline}
                onChange={(e) => setDraft({ ...draft, phoneLandline: e.target.value })}
                placeholder="01 23 45 67 89"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="c-role">Rôle</Label>
              <Select
                id="c-role"
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value as Contact['role'] })}
              >
                <option value="decision_maker">Décideur</option>
                <option value="prescriber">Prescripteur</option>
                <option value="manager">Gestionnaire</option>
                <option value="accountant">Comptable</option>
                <option value="other">Autre</option>
              </Select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-brand">
                <input
                  type="checkbox"
                  checked={draft.isPrimary}
                  onChange={(e) => setDraft({ ...draft, isPrimary: e.target.checked })}
                  className="h-4 w-4 rounded border-border text-accent focus:ring-accent/30"
                />
                Contact principal
              </label>
            </div>
          </div>

          <div>
            <Label htmlFor="c-observations">Observation</Label>
            <Textarea
              id="c-observations"
              value={draft.observations}
              onChange={(e) => setDraft({ ...draft, observations: e.target.value })}
              rows={3}
              maxLength={5000}
              placeholder="Note libre sur ce contact (propre à cette entreprise)…"
            />
          </div>

          {error && <Banner tone="danger">{error}</Banner>}

          <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
            <Button variant="secondary" onClick={onClose} type="button">
              Annuler
            </Button>
            <Button variant="primary" type="submit" loading={submitting} disabled={!canSubmit}>
              Enregistrer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Combobox de recherche de contacts existants (point 2) - design Stripe-like
// calqué sur `ProspectCombobox` de CreateOpportunityModal.
// ---------------------------------------------------------------------------
function ExistingContactCombobox({
  search,
  onSelect,
}: {
  search: (q: string) => Promise<ContactSearchResult[]>;
  onSelect: (contact: ContactSearchResult) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ContactSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        setResults(await search(term));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, open, search]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <Label htmlFor="c-search-existing">Rattacher un contact existant</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
        <Input
          id="c-search-existing"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          placeholder="Rechercher par nom ou email…"
          className="pl-8"
          autoComplete="off"
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-md border border-border-subtle bg-white shadow-popover">
          {query.trim().length < 2 ? (
            <div className="px-3 py-3 text-xs text-text-muted">
              Tapez au moins 2 caractères pour rechercher.
            </div>
          ) : loading ? (
            <div className="px-3 py-3 text-xs text-text-muted">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-text-muted">
              Aucun contact existant pour « {query.trim()} ».
            </div>
          ) : (
            <ul>
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(c);
                      setOpen(false);
                      setQuery('');
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
                  >
                    <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-brand">
                        {c.firstName} {c.lastName}
                      </div>
                      <div className="truncate text-xs text-text-muted">
                        {c.email || c.phoneMobile || c.phoneLandline || '—'}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
